import { useEffect, useMemo, useState } from 'react';
import { Spinner, Box, Divider } from '@chakra-ui/react';
import { Button, Colors, Icon, Text } from '@mercator-gcp/grout';
import normalizeUrl from 'normalize-url';
import { useParams, useNavigate } from 'react-router-dom';
import { Link } from 'components/elements/Link';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import { SidebarLayout } from 'components/layout/SidebarLayout';
import BasePageWithTopNav from 'components/layout/TopNavigationLayout';
import PageMetadata from 'components/PageMetadata';
import CommonlyWorksWith from 'components/widgets/CompanyDetails/CommonlyWorksWith';
import CompanyProjects from 'components/widgets/CompanyDetails/CompanyProjects';
import SaveCompanyToGroupModal from 'components/widgets/CompanyDetails/SaveCompanyToGroupModal';
import SuggestCorrectionFooter from 'components/widgets/SuggestCorrection/Footer';
import { useAnalytics } from 'hooks/useAnalytics';
import { useCompany } from 'hooks/useCompanies';
import { CompanyProjectLocations } from 'pages/CompanyDetails/CompanyProjectLocations';
import { CompanyDetailsPageViewSections } from 'utils/analytics/constants';
import { Z_INDEX_DICT } from 'utils/constants/zIndex';
import { formatCompanyName } from 'utils/format';
import { isVisibleCompanyURL } from 'utils/url';

const dividerStyle = {
  marginTop: 'padding_24',
  marginBottom: 'padding_24',
};

const menuItemsWrapperStyle = {
  rowGap: 'padding_32',
};

const headerStyle = {
  marginTop: 'padding_24',
};

const detailWithIconStyle = {
  display: 'flex',
  paddingY: 'padding_4',
  justifyContent: 'left',
  columnGap: 'padding_4',
};

const alignMiddleStyle = {
  alignSelf: 'center',
};

const companyInfoGridStyle = {
  padding: 'padding_16',
  paddingTop: 'padding_24',
};

const loaderStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
};

const detailsViewGridStyle = {
  paddingTop: 'padding_24',
  paddingBottom: 'padding_24',
  paddingLeft: 'padding_24',
  paddingRight: 'padding_24',
  overflow: 'hidden',
  height: '100%',
};

export const CompanyDetails = () => {
  const params = useParams();
  const company = useCompany(params.id!);
  const navigate = useNavigate();
  const { usePageViewTracking } = useAnalytics();
  const [isOpenSaveCompanyModal, setIsOpenSaveCompanyModal] = useState(false);

  usePageViewTracking({
    sectionName: CompanyDetailsPageViewSections.COMPANY_DETAILS,
    subSectionName: params?.id || undefined,
  });

  const handleSaveCompany = () => {
    setIsOpenSaveCompanyModal(true);
  };

  const isCompanyTypesVisible = useMemo(() => {
    return !!(company?.result?.company_types && company?.result?.company_types.length);
  }, [company?.result?.company_types]);

  const isCompanyWebsiteVisible = useMemo(() => {
    if (!company?.result?.website) {
      return false;
    }

    const isVisible = isVisibleCompanyURL(company?.result?.website || '');

    if (!isVisible) {
      return false;
    }

    return true;
  }, [company?.result?.website]);

  useEffect(() => {
    if (!company.loading && !company.result) {
      // No result found after request is done loading, navigate to companies directory page
      navigate('/companies');
    }
  }, [company.loading, company.result, navigate]);

  return (
    <BasePageWithTopNav>
      <PageMetadata title={`${company.result?.name}`} />
      {!company.result?.name ? (
        <Box sx={loaderStyle}>
          <Spinner
            thickness="4px"
            speed="1.65s"
            emptyColor="gray.200"
            color="brand.army-green"
            size="xl"
          />
        </Box>
      ) : (
        <SidebarLayout
          sidebarWidth="minmax(280px, 25%)"
          sidebarZIndex={Z_INDEX_DICT.COMPANY_DETAILS_MENU}
          sidebarSx={companyInfoGridStyle}
          sidebar={
            <>
              <FlexRow>
                <Button
                  startAdornment={<Icon name={Icon.Name.ChevronLeft} />}
                  variant="functional"
                  onClick={() => navigate(-1)}
                >
                  Go back
                </Button>
                <Button
                  onClick={handleSaveCompany}
                  startAdornment={<Icon name={Icon.Name.Plus} />}
                >
                  Save company
                </Button>
              </FlexRow>
              <FlexColumn sx={headerStyle}>
                <Text.SmallLabel color={Colors.TextQuiet}>Company</Text.SmallLabel>
                <Text.H2>{formatCompanyName(company.result.name)}</Text.H2>
                {isCompanyTypesVisible && (
                  <FlexRow sx={detailWithIconStyle}>
                    <Icon name={Icon.Name.CompanyOutline} />
                    <Text>{company.result.company_types?.join(', ')}</Text>
                  </FlexRow>
                )}
                {company.result.website && isCompanyWebsiteVisible && (
                  <Link
                    to={`${normalizeUrl(company.result.website)}`}
                    sx={detailWithIconStyle}
                  >
                    <Box sx={alignMiddleStyle}>
                      <Icon name={Icon.Name.Link} />
                    </Box>

                    <Text>{company.result.website}</Text>
                  </Link>
                )}
              </FlexColumn>
              <Divider sx={dividerStyle} />
              <FlexColumn sx={menuItemsWrapperStyle}>
                <CompanyProjectLocations id={params.id || ''} />
                <CommonlyWorksWith id={params.id || ''} />
                <SuggestCorrectionFooter
                  variant="inline"
                  companyId={company?.result?.uuid || undefined}
                />
              </FlexColumn>
            </>
          }
        >
          <FlexColumn sx={detailsViewGridStyle}>
            {!!company?.result?.id && (
              <CompanyProjects id={company?.result?.id} company={company?.result} />
            )}
          </FlexColumn>
        </SidebarLayout>
      )}
      {company?.result?.id && (
        <SaveCompanyToGroupModal
          isOpen={isOpenSaveCompanyModal}
          onClose={() => setIsOpenSaveCompanyModal(false)}
          onSuccess={() => setIsOpenSaveCompanyModal(false)}
          companyId={company?.result?.id}
        />
      )}
    </BasePageWithTopNav>
  );
};
